import React, { useEffect, useState } from "react";
import { useAuthFetch } from "../../lib/authFetch";
import { useCorpora, corpusLabel } from "../../lib/useCorpora";

// ── Types ────────────────────────────────────────────────────────────────────

interface SearchForm {
  q: string;
  vectorize: boolean;
  combined: boolean;
  vectorK: string;
  categories: string;
  economicSector: string;
  industrySector: string;
  geographicArea: string;
  assetClass: string;
  timePeriod: string;
  tickers: string;
  principalCompanies: string;
  author: string;
  fileIds: string;   // newline- or comma-separated UUIDs
  perPage: string;
  page: string;
  sortBy: string;
}

interface TypesenseDocument {
  id: string;
  title: string;
  content: string;
  categories: string[];
  tenant_id: string;
  created_at: number;
  metadata?: string;
  economic_sector?: string;
  industry_sector?: string;
  geographic_area?: string;
  asset_class?: string;
  time_period?: string;
  tickers?: string[];
  principal_companies?: string[];
  author?: string;
}

interface SearchHit {
  document: TypesenseDocument;
  text_match?: number;
  vector_distance?: number;
  highlights?: Array<{ field: string; snippet: string }>;
}

interface ProcessedDocumentDetails {
  file_id: string;
  owner: string;
  original_filename: string;
  new_filename?: string | null;
  bucket: string;
  endpoint: string;
  region: string;
  upload_hash: string;
  upload_hash_type: string;
  upload_s3_key: string;
  parsed_hash?: string | null;
  parsed_hash_type: string;
  parsed_s3_key?: string | null;
  analysis_s3_key?: string | null;
  document_type?: string | null;
  status: string;
  processed_at: string;
  metadata: Record<string, unknown>;
}

interface SearchResults {
  found: number;
  hits: SearchHit[];
  page: number;
  request_params?: Record<string, unknown>;
}

interface CategoryValue {
  category_type: string;
  category_value: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";
const CORPUS_API = process.env.REACT_APP_CORPUS_API_URL ?? "http://localhost:3002";

const DEFAULT_FORM: SearchForm = {
  q: "*",
  vectorize: false,
  combined: false,
  vectorK: "30",
  categories: "",
  economicSector: "",
  industrySector: "",
  geographicArea: "",
  assetClass: "",
  timePeriod: "",
  tickers: "",
  principalCompanies: "",
  author: "",
  fileIds: "",
  perPage: "20",
  page: "1",
  sortBy: "",
};

const FILTER_FIELDS: Array<{
  field: keyof SearchForm;
  label: string;
  placeholder: string;
}> = [
  { field: "categories",        label: "Categories",        placeholder: "annual-report,news" },
  { field: "economicSector",    label: "Economic Sector",   placeholder: "Financials" },
  { field: "industrySector",    label: "Industry Sector",   placeholder: "Investment Banking" },
  { field: "geographicArea",    label: "Geographic Area",   placeholder: "United States" },
  { field: "assetClass",        label: "Asset Class",       placeholder: "Equity" },
  { field: "timePeriod",        label: "Time Period",        placeholder: "FY2024" },
  { field: "tickers",           label: "Tickers",           placeholder: "JPM,GS" },
  { field: "principalCompanies",label: "Companies",         placeholder: "JPMorgan Chase" },
  { field: "author",            label: "Author",            placeholder: "Goldman Sachs" },
  { field: "perPage",           label: "Per Page",          placeholder: "20" },
  { field: "page",              label: "Page",              placeholder: "1" },
  { field: "sortBy",            label: "Sort By",           placeholder: "_text_match:desc,created_at:desc" },
];

const CATEGORY_TYPE_TO_FIELD: Record<string, keyof SearchForm> = {
  category: "categories",
  economic_sector: "economicSector",
  industry_sector: "industrySector",
  geographic_area: "geographicArea",
  asset_class: "assetClass",
  time_period: "timePeriod",
  tickers: "tickers",
  ticker: "tickers",
  principal_companies: "principalCompanies",
  principal_company: "principalCompanies",
  author: "author",
};

const COMMA_SEPARATED_FIELDS = new Set<keyof SearchForm>([
  "categories",
  "tickers",
  "principalCompanies",
]);

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildSearchBody(form: SearchForm): Record<string, unknown> {
  const splitCsv = (s: string) => s.split(/[,\n]+/).map(v => v.trim()).filter(Boolean);
  // Corpus scope is derived server-side from the caller's credentials.
  const body: Record<string, unknown> = {
    q:        form.q || "*",
    page:     parseInt(form.page || "1", 10),
    perPage:  parseInt(form.perPage || "20", 10),
  };
  if (form.combined)                   body.combined   = true;
  if (form.vectorize)                  body.vectorize  = true;
  if ((form.vectorize || form.combined) && form.vectorK) body.vectorK = parseInt(form.vectorK, 10);
  if (form.categories)          body.categories         = splitCsv(form.categories);
  if (form.economicSector)      body.economicSector      = form.economicSector;
  if (form.industrySector)      body.industrySector      = form.industrySector;
  if (form.geographicArea)      body.geographicArea      = form.geographicArea;
  if (form.assetClass)          body.assetClass          = form.assetClass;
  if (form.timePeriod)          body.timePeriod          = form.timePeriod;
  if (form.tickers)             body.tickers             = splitCsv(form.tickers);
  if (form.principalCompanies)  body.principalCompanies  = splitCsv(form.principalCompanies);
  if (form.author)              body.author              = form.author;
  if (form.fileIds)             body.fileIds             = splitCsv(form.fileIds);
  if (form.sortBy)              body.sortBy              = form.sortBy;
  return body;
}

function parseMetadata(raw?: string): Record<string, unknown> | null {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getFileId(doc: TypesenseDocument): string | null {
  const meta = parseMetadata(doc.metadata);
  const fileId = meta?.fileId;
  return typeof fileId === "string" && fileId.trim() ? fileId : null;
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

/** Source page numbers recorded on the chunk at indexing time. */
function getPageNums(doc: TypesenseDocument): number[] {
  const meta = parseMetadata(doc.metadata);
  const raw = meta?.pageNums;
  if (!Array.isArray(raw)) return [];
  return raw.filter((n): n is number => typeof n === "number");
}

/** A chunk's reference to a table/figure in the parsed docling artifact
 *  (#38 phase 2), recorded by the indexer in metadata.anchors. */
interface ElementAnchor {
  type: "table" | "figure";
  page: number;      // 1-based physical page
  idx: number;       // element ordinal on the page
  figureType?: string;
  caption?: string;
  text: string;      // element's searchable text, for match attribution
}

function getAnchors(doc: TypesenseDocument): ElementAnchor[] {
  const meta = parseMetadata(doc.metadata);
  const raw = meta?.anchors;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is ElementAnchor =>
      !!a && typeof a === "object" &&
      ((a as ElementAnchor).type === "table" || (a as ElementAnchor).type === "figure") &&
      typeof (a as ElementAnchor).page === "number" &&
      typeof (a as ElementAnchor).idx === "number",
  );
}

/** Terms the search engine matched, from Typesense highlight data. */
function getMatchedTokens(hit: SearchHit): string[] {
  const tokens = new Set<string>();
  for (const h of hit.highlights ?? []) {
    const raw = h as { matched_tokens?: unknown; snippet?: string };
    if (Array.isArray(raw.matched_tokens)) {
      for (const t of raw.matched_tokens) if (typeof t === "string" && t.length > 1) tokens.add(t.toLowerCase());
    }
    if (typeof raw.snippet === "string") {
      for (const m of raw.snippet.matchAll(/<mark>(.*?)<\/mark>/g)) {
        if (m[1].length > 1) tokens.add(m[1].toLowerCase());
      }
    }
  }
  return [...tokens];
}

function anchorLabel(a: ElementAnchor): string {
  const kind =
    a.type === "table" ? "Table"
    : a.figureType === "infographic" ? "Infographic"
    : a.figureType === "chart" ? "Chart"
    : "Image";
  const caption = a.caption && a.caption.length > 28 ? `${a.caption.slice(0, 28)}…` : a.caption;
  return `${kind} p.${a.page}${caption ? ` — ${caption}` : ""}`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

const CHIP_COLORS: Record<string, string> = {
  purple: "bg-purple-50 text-purple-700 border-purple-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  green:  "bg-green-50  text-green-700  border-green-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  pink:   "bg-pink-50   text-pink-700   border-pink-200",
  teal:   "bg-teal-50   text-teal-700   border-teal-200",
  blue:   "bg-blue-50   text-blue-700   border-blue-200",
  gray:   "bg-paper  text-kslate   border-krule",
};

const Chip: React.FC<{ label: string; value: string; color?: string }> = ({
  label, value, color = "gray",
}) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${CHIP_COLORS[color] ?? CHIP_COLORS.gray}`}>
    <span className="opacity-50 font-medium">{label}</span>
    <span>{value}</span>
  </span>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({
  checked, onChange, label,
}) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-kamber/70 focus:ring-offset-1 ${
        checked ? "bg-ink" : "bg-krule"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
    <span className="text-sm text-inklight">{label}</span>
  </label>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-kslate">{label}</div>
      <div className="sm:col-span-2 text-ink break-words">{value}</div>
    </div>
  );
};

// ── Result Card ──────────────────────────────────────────────────────────────

// ── Source page modal ────────────────────────────────────────────────────────
// Renders one page of the parsed document via the corpus-api element API
// (#38): text blocks, tables, figures. Figure images are lazy-loaded from the
// single-figure endpoint, since the page endpoint deliberately omits them.

interface ParsedPageTable {
  idx: number;
  headers?: (string | null)[];
  rows?: (string | null)[][];
  caption?: string;
  description?: string;
}

interface ParsedPageFigure {
  idx: number;
  figure_type?: string;
  description?: string;
  hasImage: boolean;
}

interface ParsedPageResponse {
  fileId: string;
  pageNum: number;
  textBlocks: (string | { text?: string; label?: string })[];
  tables: ParsedPageTable[];
  figures: ParsedPageFigure[];
}

const ParsedTableBlock: React.FC<{ tbl: ParsedPageTable }> = ({ tbl }) => (
  <div className="border border-blue-200 rounded-lg p-3">
    <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide mb-2">
      {tbl.caption ? `Table — ${tbl.caption}` : `Table ${tbl.idx + 1}`}
    </p>
    {tbl.description && (
      <p className="text-xs text-inklight italic mb-2">{tbl.description}</p>
    )}
    {(tbl.rows?.length ?? 0) > 0 && (
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          {(tbl.headers?.length ?? 0) > 0 && (
            <thead>
              <tr>
                {tbl.headers!.map((h, i) => (
                  <th key={i} className="border border-krule bg-paper px-2 py-1 text-left font-semibold text-inklight">
                    {h ?? ""}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {tbl.rows!.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-paper/50"}>
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => (
                  <td key={ci} className="border border-krule px-2 py-1 text-inklight align-top">
                    {cell ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const FigureImage: React.FC<{ fileId: string; pageNum: number; idx: number }> = ({ fileId, pageNum, idx }) => {
  const authFetch = useAuthFetch();
  const [imageB64, setImageB64] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(
          `${CORPUS_API}/api/uploads/${encodeURIComponent(fileId)}/parsed/pages/${pageNum}/figures/${idx}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { figure?: { image_b64?: string | null } };
        if (!cancelled) setImageB64(body.figure?.image_b64 ?? null);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, fileId, pageNum, idx]);

  if (failed) return <p className="text-xs text-kfaint italic">Image unavailable.</p>;
  if (!imageB64) return <p className="text-xs text-kfaint italic">Loading image…</p>;
  return (
    <img
      src={`data:image/png;base64,${imageB64}`}
      alt={`Figure ${idx + 1}`}
      className="max-w-full rounded border border-krule"
    />
  );
};

const SourcePageModal: React.FC<{
  fileId: string;
  pageNum: number;
  title: string;
  onClose: () => void;
}> = ({ fileId, pageNum, title, onClose }) => {
  const authFetch = useAuthFetch();
  const [page, setPage] = useState<ParsedPageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(
          `${CORPUS_API}/api/uploads/${encodeURIComponent(fileId)}/parsed/pages/${pageNum}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        if (!cancelled) setPage(body as ParsedPageResponse);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load page");
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, fileId, pageNum]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex flex-col w-full max-w-3xl mx-4 max-h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-krule shrink-0">
          <span className="text-sm font-medium text-inklight truncate">
            {title} · Page {pageNum}
          </span>
          <div className="flex items-center gap-3 shrink-0 ml-4">
            <a
              href={`${CORPUS_API}/api/uploads/${encodeURIComponent(fileId)}/stream#page=${pageNum}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-blue-700 hover:underline"
            >
              Open PDF ↗
            </a>
            <button onClick={onClose} className="text-kfaint hover:text-inklight text-xl leading-none">×</button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!error && !page && <p className="text-sm text-kfaint">Loading page…</p>}

          {page && (
            <>
              {page.textBlocks.length > 0 && (
                <div className="space-y-2">
                  {page.textBlocks.map((block, i) => {
                    const text = typeof block === "string" ? block : block.text ?? "";
                    if (!text.trim()) return null;
                    const isHeader = typeof block !== "string" && block.label === "section_header";
                    return isHeader ? (
                      <p key={i} className="text-sm font-semibold text-ink">{text}</p>
                    ) : (
                      <p key={i} className="text-sm text-inklight leading-relaxed">{text}</p>
                    );
                  })}
                </div>
              )}

              {page.tables.map((tbl) => (
                <ParsedTableBlock key={`t${tbl.idx}`} tbl={tbl} />
              ))}

              {page.figures.map((fig) => (
                <div key={`f${fig.idx}`} className="border border-purple-200 rounded-lg p-3">
                  <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-wide mb-2">
                    {fig.figure_type ? `Figure ${fig.idx + 1} — ${fig.figure_type}` : `Figure ${fig.idx + 1}`}
                  </p>
                  {fig.hasImage && <FigureImage fileId={fileId} pageNum={pageNum} idx={fig.idx} />}
                  {fig.description && (
                    <p className="text-xs text-inklight leading-relaxed mt-2">{fig.description}</p>
                  )}
                </div>
              ))}

              {page.textBlocks.length === 0 && page.tables.length === 0 && page.figures.length === 0 && (
                <p className="text-sm text-kfaint">This page has no parsed content.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Element modal ────────────────────────────────────────────────────────────
// Shows one table or figure from the parsed docling artifact, addressed by an
// ElementAnchor, fetched from the corpus-api element endpoints.

const ElementModal: React.FC<{
  fileId: string;
  anchor: ElementAnchor;
  title: string;
  onClose: () => void;
}> = ({ fileId, anchor, title, onClose }) => {
  const authFetch = useAuthFetch();
  const [table, setTable] = useState<ParsedPageTable | null>(null);
  const [figure, setFigure] = useState<{
    figure_type?: string;
    description?: string;
    image_b64?: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const kind = anchor.type === "table" ? "tables" : "figures";
        const res = await authFetch(
          `${CORPUS_API}/api/uploads/${encodeURIComponent(fileId)}/parsed/pages/${anchor.page}/${kind}/${anchor.idx}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        if (cancelled) return;
        if (anchor.type === "table") {
          setTable({ ...(body as { table: Omit<ParsedPageTable, "idx"> }).table, idx: anchor.idx });
        } else {
          setFigure((body as { figure: NonNullable<typeof figure> }).figure);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load element");
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, fileId, anchor]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex flex-col w-full max-w-3xl mx-4 max-h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-krule shrink-0">
          <span className="text-sm font-medium text-inklight truncate">
            {title} · {anchorLabel(anchor)}
          </span>
          <button onClick={onClose} className="ml-4 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!error && !table && !figure && <p className="text-sm text-kfaint">Loading…</p>}

          {table && <ParsedTableBlock tbl={table} />}

          {figure && (
            <div className="border border-purple-200 rounded-lg p-3">
              {figure.figure_type && (
                <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-wide mb-2">
                  {figure.figure_type}
                </p>
              )}
              {figure.image_b64 && (
                <img
                  src={`data:image/png;base64,${figure.image_b64}`}
                  alt={figure.description || "Figure"}
                  className="max-w-full rounded border border-krule"
                />
              )}
              {figure.description && (
                <p className="text-xs text-inklight leading-relaxed mt-2">{figure.description}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultCard: React.FC<{
  hit: SearchHit;
  index: number;
  onViewDetails: (fileId: string) => void;
  detailsLoadingId: string | null;
}> = ({ hit, index, onViewDetails, detailsLoadingId }) => {
  const doc = hit.document;
  const fileId = getFileId(doc);
  const meta = parseMetadata(doc.metadata);
  const pageNums = getPageNums(doc);
  const anchors = getAnchors(doc);
  const matchedTokens = getMatchedTokens(hit);
  // Elements whose own text contains a matched search term — i.e. the hit is
  // (at least partly) inside this table/figure. Without highlight data we
  // can't attribute, so we fall back to offering every element of the chunk.
  const matchedAnchors = matchedTokens.length > 0
    ? anchors.filter((a) => a.text && matchedTokens.some((t) => a.text.toLowerCase().includes(t)))
    : anchors;
  const [expanded, setExpanded] = useState(false);
  const [sourcePage, setSourcePage] = useState<number | null>(null);
  const [openAnchor, setOpenAnchor] = useState<ElementAnchor | null>(null);

  return (
    <div className="bg-white border border-krule rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-6 h-6 bg-paper rounded text-xs text-kfaint flex items-center justify-center font-mono mt-0.5">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-ink text-sm leading-snug">
              {doc.title || doc.id}
            </h3>
            <p className="text-xs text-kfaint mt-0.5 font-mono truncate">{doc.id}</p>
            <p className="text-xs text-kslate mt-0.5 font-mono truncate">
              fileId: {fileId ?? "unavailable"}
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1 text-xs text-kfaint">
            {hit.text_match !== undefined && (
              <span className="bg-paper px-1.5 py-0.5 rounded font-mono">
                bm25 {hit.text_match.toLocaleString()}
              </span>
            )}
            {hit.vector_distance !== undefined && (
              <span className="bg-blue-50 text-ink px-1.5 py-0.5 rounded font-mono">
                dist {hit.vector_distance.toFixed(4)}
              </span>
            )}
            {doc.created_at && (
              <span>{formatDate(doc.created_at)}</span>
            )}
          </div>
        </div>

        <div className="mt-3 ml-9 flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileId && onViewDetails(fileId)}
            disabled={!fileId || detailsLoadingId === fileId}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {!fileId
              ? "No file ID"
              : detailsLoadingId === fileId
                ? "Loading details…"
                : "View details"}
          </button>
        </div>

        {/* Source link: the chunk's origin pages, each opening the parsed
            page in a modal; plus a direct link to the original PDF. */}
        {fileId && pageNums.length > 0 && (
          <div className="mt-2 ml-9 flex items-center flex-wrap gap-1.5 text-xs text-kslate">
            <span className="text-kfaint">Source:</span>
            {pageNums.slice(0, 8).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setSourcePage(n)}
                className="px-2 py-0.5 rounded border border-krule bg-paper font-mono hover:bg-kamber/10 hover:border-blue-200 hover:text-blue-700 transition-colors"
                title={`View parsed page ${n}`}
              >
                p.{n}
              </button>
            ))}
            {pageNums.length > 8 && (
              <span className="text-kfaint">+{pageNums.length - 8} more</span>
            )}
            <a
              href={`${CORPUS_API}/api/uploads/${encodeURIComponent(fileId)}/stream#page=${pageNums[0]}`}
              target="_blank"
              rel="noreferrer"
              className="ml-1 text-blue-700 hover:underline"
            >
              PDF ↗
            </a>
          </div>
        )}

        {/* Element references: tables/figures the search term hit (or, when
            highlight data is unavailable, all of the chunk's elements). */}
        {fileId && matchedAnchors.length > 0 && (
          <div className="mt-1.5 ml-9 flex items-center flex-wrap gap-1.5 text-xs">
            <span className="text-kfaint">{matchedTokens.length > 0 ? "Hit in:" : "Elements:"}</span>
            {matchedAnchors.slice(0, 6).map((a) => (
              <button
                key={`${a.type}-${a.page}-${a.idx}`}
                type="button"
                onClick={() => setOpenAnchor(a)}
                title={`View ${a.type} from the parsed document`}
                className={`px-2 py-0.5 rounded border transition-colors ${
                  a.type === "table"
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100"
                }`}
              >
                {anchorLabel(a)}
              </button>
            ))}
            {matchedAnchors.length > 6 && (
              <span className="text-kfaint">+{matchedAnchors.length - 6} more</span>
            )}
          </div>
        )}

        {fileId && sourcePage !== null && (
          <SourcePageModal
            fileId={fileId}
            pageNum={sourcePage}
            title={doc.title || doc.id}
            onClose={() => setSourcePage(null)}
          />
        )}

        {fileId && openAnchor && (
          <ElementModal
            fileId={fileId}
            anchor={openAnchor}
            title={doc.title || doc.id}
            onClose={() => setOpenAnchor(null)}
          />
        )}

        {/* Category tags */}
        {doc.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 ml-9">
            {doc.categories.map((cat) => (
              <span key={cat} className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs">
                {cat}
              </span>
            ))}
          </div>
        )}

        {/* Classification facets */}
        {(doc.economic_sector || doc.industry_sector || doc.geographic_area ||
          doc.asset_class || doc.time_period || doc.author ||
          (doc.tickers && doc.tickers.length > 0) ||
          (doc.principal_companies && doc.principal_companies.length > 0)) && (
          <div className="flex flex-wrap gap-1 mt-2 ml-9">
            {doc.economic_sector  && <Chip label="sector"   value={doc.economic_sector}  color="purple" />}
            {doc.industry_sector  && <Chip label="industry" value={doc.industry_sector}  color="indigo" />}
            {doc.geographic_area  && <Chip label="geo"      value={doc.geographic_area}  color="green"  />}
            {doc.asset_class      && <Chip label="asset"    value={doc.asset_class}       color="yellow" />}
            {doc.time_period      && <Chip label="period"   value={doc.time_period}       color="orange" />}
            {doc.author           && <Chip label="author"   value={doc.author}            color="pink"   />}
            {doc.tickers?.map((t) => <Chip key={t} label="ticker" value={t} color="teal" />)}
            {doc.principal_companies?.map((c) => <Chip key={c} label="co" value={c} color="gray" />)}
          </div>
        )}

        {/* Content snippet */}
        {doc.content && (
          <div className="mt-3 ml-9">
            <p className={`text-xs text-kslate leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
              {doc.content}
            </p>
            {doc.content.length > 300 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-ksky hover:text-blue-700 mt-1"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}

        {/* Metadata summary */}
        {meta && (
          <div className="mt-2 ml-9 flex flex-wrap gap-x-3 text-xs text-kfaint font-mono">
            {typeof meta.type === "string"      && <span>type: {meta.type}</span>}
            {typeof meta.pageCount === "number" && <span>pages: {meta.pageCount}</span>}
            {typeof meta.sectionCount === "number" && <span>sections: {meta.sectionCount}</span>}
            {typeof meta.category === "string"  && <span>category: {meta.category}</span>}
            {typeof meta.analysisUnit === "string" && <span>unit: {meta.analysisUnit}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SearchTab: React.FC = () => {
  const [form, setForm] = useState<SearchForm>(DEFAULT_FORM);
  const [results, setResults]   = useState<SearchResults | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [lastUrl, setLastUrl]   = useState<string>("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoadingId, setDetailsLoadingId] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [documentDetails, setDocumentDetails] = useState<ProcessedDocumentDetails | null>(null);
  const [fieldSuggestions, setFieldSuggestions] = useState<Partial<Record<keyof SearchForm, string[]>>>({});
  // Active corpora for the search. null = all accessible corpora (the server
  // default: everything the user owns or subscribes to).
  const [activeCorpora, setActiveCorpora] = useState<Set<string> | null>(null);
  const authFetch = useAuthFetch();
  const { accessible: accessibleCorpora } = useCorpora();

  const toggleCorpus = (corpusId: string) => {
    setActiveCorpora((prev) => {
      const all = accessibleCorpora.map((c) => c.corpusId);
      const next = new Set(prev ?? all);
      if (next.has(corpusId)) next.delete(corpusId);
      else next.add(corpusId);
      // Empty or complete selection both mean "search everything".
      if (next.size === 0 || next.size === all.length) return null;
      return next;
    });
  };

  const setField = (field: keyof SearchForm, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    let cancelled = false;

    const loadCategoryValues = async () => {
      try {
        const resp = await authFetch(`${API_BASE}/query/category-values`);
        const data = await resp.json();

        if (!resp.ok || !Array.isArray(data) || cancelled) {
          return;
        }

        const next: Partial<Record<keyof SearchForm, Set<string>>> = {};

        for (const row of data as CategoryValue[]) {
          const field = CATEGORY_TYPE_TO_FIELD[row.category_type];
          if (!field || !row.category_value) continue;
          if (!next[field]) next[field] = new Set<string>();
          next[field]!.add(row.category_value);
        }

        const normalized: Partial<Record<keyof SearchForm, string[]>> = {};
        for (const [field, values] of Object.entries(next) as Array<[keyof SearchForm, Set<string>]>) {
          normalized[field] = Array.from(values).sort((a, b) => a.localeCompare(b));
        }

        setFieldSuggestions(normalized);
      } catch {
        // Suggestions are optional; failures should not block search UI.
      }
    };

    loadCategoryValues();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const applySuggestion = (field: keyof SearchForm, suggestion: string) => {
    if (!COMMA_SEPARATED_FIELDS.has(field)) {
      setField(field, suggestion);
      return;
    }

    const currentRaw = String(form[field] ?? "");
    const currentValues = currentRaw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (currentValues.some((v) => v.toLowerCase() === suggestion.toLowerCase())) {
      return;
    }

    const nextValues = [...currentValues, suggestion];
    setField(field, nextValues.join(","));
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsLoadingId(null);
    setDetailsError(null);
    setDocumentDetails(null);
  };

  const handleViewDetails = async (fileId: string) => {
    setDetailsOpen(true);
    setDetailsLoadingId(fileId);
    setDetailsError(null);
    setDocumentDetails(null);

    try {
      const resp = await authFetch(`${API_BASE}/query/document/${encodeURIComponent(fileId)}`);
      const data = await resp.json();

      if (!resp.ok) {
        setDetailsError((data as { error?: string }).error ?? `HTTP ${resp.status}`);
        return;
      }

      setDocumentDetails(data as ProcessedDocumentDetails);
    } catch (err) {
      setDetailsError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetailsLoadingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const firstPageForm = { ...form, page: "1" };
    const searchBody = buildSearchBody(firstPageForm);
    if (activeCorpora && activeCorpora.size > 0) searchBody.corpusIds = Array.from(activeCorpora);
    setLastUrl(`POST ${API_BASE}/query/search ${JSON.stringify(searchBody)}`);
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const resp = await authFetch(`${API_BASE}/query/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(searchBody),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError((data as { error?: string }).error ?? `HTTP ${resp.status}`);
        return;
      }

      // A Search click should issue one search request. The explicit page field
      // remains the way to navigate deeper result pages.
      setResults(data as SearchResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Primary row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {/* Query */}
          <div className="md:col-span-5">
            <label className="block text-xs font-medium text-kslate mb-1">Query</label>
            <input
              type="text"
              value={form.q}
              onChange={(e) => setField("q", e.target.value)}
              placeholder='* to browse all'
              className="w-full border border-krule rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kamber/70"
            />
          </div>
          {/* Vector toggle + Combined + K */}
          <div className="md:col-span-7 flex items-end gap-3 pb-[10px]">
            <Toggle
              checked={form.vectorize}
              onChange={(v) => setField("vectorize", v)}
              label="Vector search"
            />
            <Toggle
              checked={form.combined}
              onChange={(v) => setField("combined", v)}
              label="Combined"
            />
            {(form.vectorize || form.combined) && (
              <div className="w-20">
                <label className="block text-xs font-medium text-kslate mb-1">k</label>
                <input
                  type="number"
                  min={1}
                  value={form.vectorK}
                  onChange={(e) => setField("vectorK", e.target.value)}
                  className="w-full border border-krule rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kamber/70"
                />
              </div>
            )}
          </div>
        </div>

        {/* Active corpora — all on by default; click to exclude/include */}
        {accessibleCorpora.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-kslate">Corpora:</span>
            {accessibleCorpora.map((c) => {
              const active = activeCorpora === null || activeCorpora.has(c.corpusId);
              return (
                <button
                  key={c.corpusId}
                  type="button"
                  onClick={() => toggleCorpus(c.corpusId)}
                  title={c.corpusId}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    active
                      ? "bg-kamber/15 border-kamber/50 text-ink"
                      : "bg-white border-krule text-kfaint hover:text-kslate"
                  }`}
                >
                  {corpusLabel(c)}
                </button>
              );
            })}
            {activeCorpora !== null && (
              <button
                type="button"
                onClick={() => setActiveCorpora(null)}
                className="text-xs text-kslate hover:text-ink underline underline-offset-2"
              >
                all
              </button>
            )}
          </div>
        )}

        {/* Collapsible filters */}
        <div className="border border-krule rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-inklight hover:bg-paper/50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span>Filters</span>
              {Object.entries(form).some(([k, v]) =>
                !["q", "vectorize", "combined", "vectorK", "perPage", "page", "sortBy"].includes(k) && v
              ) && (
                <span className="w-2 h-2 bg-ksky rounded-full" />
              )}
            </span>
            <span className="text-kfaint text-xs">{showFilters ? "▲" : "▼"}</span>
          </button>
          {showFilters && (
            <div className="border-t border-krule px-4 py-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 bg-paper/50">
              {FILTER_FIELDS.map(({ field, label, placeholder }) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-kslate mb-1">{label}</label>
                  {(() => {
                    const suggestions = fieldSuggestions[field] ?? [];
                    const inputValue = String(form[field] ?? "");
                    const queryToken = COMMA_SEPARATED_FIELDS.has(field)
                      ? inputValue.split(",").pop()?.trim().toLowerCase() ?? ""
                      : inputValue.trim().toLowerCase();
                    const showSuggestions = queryToken.length >= 1;
                    const filteredSuggestions = suggestions
                      .filter((value) => {
                        if (!showSuggestions) return false;
                        return value.toLowerCase().includes(queryToken);
                      })
                      .slice(0, 8);
                    const listId = `suggestions-${field}`;

                    return (
                      <>
                  <input
                    type="text"
                    value={form[field] as string}
                    onChange={(e) => setField(field, e.target.value)}
                    placeholder={placeholder}
                    list={suggestions.length > 0 ? listId : undefined}
                    className="w-full border border-krule rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
                  />
                        {suggestions.length > 0 && (
                          <datalist id={listId}>
                            {suggestions.map((value) => (
                              <option key={value} value={value} />
                            ))}
                          </datalist>
                        )}

                        {showSuggestions && filteredSuggestions.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {filteredSuggestions.map((value) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => applySuggestion(field, value)}
                                className="px-2 py-0.5 rounded border border-krule bg-white text-[11px] text-kslate hover:bg-kamber/10 hover:border-blue-200 hover:text-blue-700 transition-colors"
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
          {showFilters && (
            <div className="border-t border-krule px-4 pb-4 bg-paper/50">
              <label className="block text-xs font-medium text-kslate mb-1 mt-3">
                Document IDs <span className="text-kfaint font-normal">(one UUID per line, or comma-separated — leave blank to search all)</span>
              </label>
              <textarea
                rows={3}
                value={form.fileIds}
                onChange={(e) => setField("fileIds", e.target.value)}
                placeholder={"e4245ecc-ecf8-43e8-85f7-4da646703d4a\nabc12345-..."}
                className="w-full border border-krule rounded-md px-3 py-1.5 text-sm bg-white font-mono focus:outline-none focus:ring-2 focus:ring-kamber/70"
              />
            </div>
          )}
        </div>

        {/* Action row */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 bg-ink text-white rounded-md text-sm font-medium hover:bg-inklight disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Searching…
              </span>
            ) : "Search"}
          </button>
          <button
            type="button"
            onClick={() => { setForm(DEFAULT_FORM); setResults(null); setError(null); setLastUrl(""); }}
            className="px-4 py-2 text-sm text-kslate hover:text-ink transition-colors"
          >
            Reset
          </button>
          {results && (
            <span className="ml-auto text-sm text-kslate">
              <span className="font-semibold text-ink">{results.found.toLocaleString()}</span> result{results.found !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </form>

      {/* Effective URL (debug) */}
      {lastUrl && (
        <div className="mt-3 px-3 py-2 bg-paper/50 border border-krule rounded text-xs font-mono text-kslate break-all">
          {lastUrl}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Empty state */}
      {results && results.hits.length === 0 && !error && (
        <div className="mt-8 text-center py-12 text-kfaint">
          <div className="text-3xl mb-2">∅</div>
          <div className="text-sm">No results found</div>
        </div>
      )}

      {/* Results */}
      {results && results.hits.length > 0 && (
        <div className="mt-6 space-y-3">
          {results.hits.map((hit, i) => (
            <ResultCard
              key={hit.document.id ?? i}
              hit={hit}
              index={i}
              onViewDetails={handleViewDetails}
              detailsLoadingId={detailsLoadingId}
            />
          ))}
        </div>
      )}

      {/* Details modal */}
      {detailsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-kterm/60 p-4"
          onClick={closeDetails}
        >
          <div
            className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-krule"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-krule px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">Document details</h3>
                <p className="text-sm text-kslate">Fetched from /query/document/:fileId</p>
              </div>
              <button
                type="button"
                onClick={closeDetails}
                className="rounded-md px-3 py-1.5 text-sm text-kslate hover:text-ink hover:bg-paper transition-colors"
              >
                Close
              </button>
            </div>

            <div className="max-h-[calc(85vh-73px)] overflow-auto px-5 py-4 space-y-4">
              {detailsLoadingId && (
                <div className="text-sm text-kslate">Loading document details…</div>
              )}

              {detailsError && (
                <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                  {detailsError}
                </div>
              )}

              {documentDetails && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DetailRow label="File ID" value={<span className="font-mono text-xs">{documentDetails.file_id}</span>} />
                    <DetailRow label="Owner" value={documentDetails.owner} />
                    <DetailRow label="Original filename" value={documentDetails.original_filename} />
                    <DetailRow label="New filename" value={documentDetails.new_filename ?? "-"} />
                    <DetailRow label="Status" value={documentDetails.status} />
                    <DetailRow label="Processed at" value={documentDetails.processed_at} />
                    <DetailRow label="Bucket" value={documentDetails.bucket} />
                    <DetailRow label="Endpoint" value={documentDetails.endpoint} />
                    <DetailRow label="Region" value={documentDetails.region} />
                    <DetailRow label="Document type" value={documentDetails.document_type ?? "-"} />
                    <DetailRow label="Upload hash" value={<span className="font-mono text-xs break-all">{documentDetails.upload_hash}</span>} />
                    <DetailRow label="Upload hash type" value={documentDetails.upload_hash_type} />
                    <DetailRow label="Upload S3 key" value={<span className="font-mono text-xs break-all">{documentDetails.upload_s3_key}</span>} />
                    <DetailRow label="Parsed hash" value={documentDetails.parsed_hash ? <span className="font-mono text-xs break-all">{documentDetails.parsed_hash}</span> : "-"} />
                    <DetailRow label="Parsed hash type" value={documentDetails.parsed_hash_type} />
                    <DetailRow label="Parsed S3 key" value={documentDetails.parsed_s3_key ? <span className="font-mono text-xs break-all">{documentDetails.parsed_s3_key}</span> : "-"} />
                    <DetailRow label="Analysis S3 key" value={documentDetails.analysis_s3_key ? <span className="font-mono text-xs break-all">{documentDetails.analysis_s3_key}</span> : "-"} />
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-ink mb-2">Metadata</h4>
                    <pre className="rounded-lg bg-kterm text-ktermtext p-4 text-xs overflow-auto whitespace-pre-wrap break-words max-h-72">
                      {JSON.stringify(documentDetails.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchTab;
